import * as Sentry from '@sentry/react';
import { ComponentType, ErrorInfo, useCallback } from 'react';
import {
    ErrorBoundary as ReactErrorBoundary,
    ErrorBoundaryProps as ReactErrorBoundaryProps,
    FallbackProps,
    useErrorBoundary,
    withErrorBoundary,
} from 'react-error-boundary';

import { DefaultErrorFallback } from './DefaultErrorFallback';

/**
 * Converts a boundary name to a kebab-case test ID.
 * Handles PascalCase, camelCase, spaces, and special characters.
 * @example "Dashboard" -> "dashboard-error-boundary"
 * @example "LocationOverview" -> "location-overview-error-boundary"
 * @example "Location Overview" -> "location-overview-error-boundary"
 * @example "Dashboard_View" -> "dashboard-view-error-boundary"
 */
function toTestId(boundaryName?: string): string {
    if (!boundaryName) {
        return 'error-boundary';
    }
    return `${boundaryName
        .trim() // Remove leading/trailing whitespace
        .replace(/[_/:\\]/g, '-') // Replace common separators with dashes
        .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove other special chars
        .replace(/\s+/g, '-') // Replace spaces with dashes
        .replace(/([A-Z])/g, '-$1') // Add dash before capitals
        .toLowerCase()
        .replace(/^-/, '') // Remove leading dash
        .replace(/-+/g, '-')}-error-boundary`; // Clean up multiple dashes
}

type SentryCaptureOptions = {
    transformSentryScope:
        | ((scope: Sentry.Scope, context: { error: Error; errorInfo: ErrorInfo }) => void)
        | undefined;
    error: Error;
    errorInfo: ErrorInfo;
    boundaryName: string | undefined;
};

/**
 * Helper function to capture errors to Sentry with proper scope management.
 * Handles both custom scope transformations and direct captures.
 */
function doSentryCapture({
    transformSentryScope,
    error,
    errorInfo,
    boundaryName,
}: SentryCaptureOptions) {
    if (transformSentryScope) {
        // Use withScope for custom context
        Sentry.withScope((scope) => {
            try {
                transformSentryScope(scope, { error, errorInfo });
            } catch (transformError) {
                // Log but don't throw - we still want to capture the error
                // eslint-disable-next-line no-console
                console.error('Error in transformSentryScope callback:', transformError);
                // Continue with default context
            }

            // Always set boundary name and react context
            if (boundaryName) {
                scope.setTag('errorBoundary', boundaryName);
            }
            scope.setContext('react', {
                componentStack: errorInfo.componentStack,
            });

            Sentry.captureException(error);
        });
    } else {
        // Direct capture without custom context
        Sentry.captureException(error, {
            contexts: {
                react: {
                    componentStack: errorInfo.componentStack,
                },
            },
            tags: boundaryName ? { errorBoundary: boundaryName } : undefined,
        });
    }
}

/**
 * Props for the unified ErrorBoundary component.
 * Extends react-error-boundary props with Sentry-specific features.
 */
export interface AppErrorBoundaryProps extends Omit<ReactErrorBoundaryProps, 'onError'> {
    /**
     * Name identifier for this error boundary.
     * - Used as Sentry tag for filtering errors in dashboard
     * - Used as data-testid for E2E testing (converted to kebab-case)
     *
     * @example
     * ```tsx
     * <ErrorBoundary boundaryName="Dashboard">
     *   // Sentry tag: errorBoundary="Dashboard"
     *   // Test ID: data-testid="dashboard-error-boundary"
     * </ErrorBoundary>
     * ```
     */
    boundaryName?: string;

    /**
     * Callback to customize Sentry scope before error capture.
     * Use this to add custom tags, context, or breadcrumbs.
     *
     * @param scope - Sentry scope to modify
     * @param context - Object containing error and errorInfo
     *
     * @example
     * ```tsx
     * <ErrorBoundary
     *   transformSentryScope={(scope, { error, errorInfo }) => {
     *     scope.setTag('activeTab', currentTab);
     *     scope.setContext('workbench', { indicators: selectedIndicators });
     *   }}
     * >
     * ```
     */
    transformSentryScope?: (
        scope: Sentry.Scope,
        context: { error: Error; errorInfo: ErrorInfo }
    ) => void;

    /**
     * Filter function to skip Sentry capture for specific errors.
     * Return true to skip Sentry capture (error boundary still renders fallback).
     * By default, all errors are captured to Sentry.
     *
     * @param error - The error that was caught
     * @param errorInfo - React error info with component stack
     * @returns true to skip capture, false to capture (default)
     */
    skipSentryCapture?: (error: Error, errorInfo: ErrorInfo) => boolean;

    /**
     * Custom error handler called after Sentry capture.
     * Overrides the onError prop from react-error-boundary with proper typing.
     *
     * @param error - The error that was caught
     * @param errorInfo - React error info with component stack
     */
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

/**
 * Unified ErrorBoundary component that combines Sentry error capture
 * with react-error-boundary features.
 *
 * Features:
 * - Automatic Sentry error capture with React component context
 * - Supports all react-error-boundary features (resetKeys, onReset, etc.)
 * - Robust error handling (graceful Sentry failures)
 * - Customizable error filtering and Sentry scope modification
 *
 * @example Basic usage
 * ```tsx
 * <ErrorBoundary fallback={<ErrorPage />} boundaryName="App">
 *   <App />
 * </ErrorBoundary>
 * ```
 *
 * @example With resetKeys (auto-reset on prop changes)
 * ```tsx
 * <ErrorBoundary
 *   FallbackComponent={ErrorFallback}
 *   resetKeys={[userId, locationId]}
 *   boundaryName="Dashboard"
 * >
 *   <Dashboard />
 * </ErrorBoundary>
 * ```
 *
 * @example With custom Sentry context
 * ```tsx
 * <ErrorBoundary
 *   boundaryName="Workbench"
 *   transformSentryScope={(scope, { error, errorInfo }) => {
 *     scope.setTag('activeTab', currentTab);
 *     scope.setContext('workbench', { indicators: selectedIndicators });
 *   }}
 * >
 *   <Workbench />
 * </ErrorBoundary>
 * ```
 */
export function ErrorBoundary({
    onError,
    boundaryName,
    transformSentryScope,
    skipSentryCapture,
    fallback,
    FallbackComponent,
    fallbackRender,
    ...reactErrorBoundaryProps
}: AppErrorBoundaryProps) {
    /**
     * Error handler that captures to Sentry and calls custom onError.
     * Uses try-catch-finally to ensure onError always executes even if Sentry fails.
     * Memoized to prevent recreation on every render.
     */
    const handleError = useCallback(
        (error: Error, errorInfo: ErrorInfo) => {
            try {
                // Check if we should skip capturing this error
                if (skipSentryCapture && skipSentryCapture(error, errorInfo)) {
                    return;
                }

                // Capture to Sentry using helper function
                doSentryCapture({
                    transformSentryScope,
                    error,
                    errorInfo,
                    boundaryName,
                });
            } catch (sentryError) {
                // Log but don't throw - error boundary should still work even if Sentry fails
                // eslint-disable-next-line no-console
                console.error('Failed to capture error to Sentry:', sentryError);

                // In development, show more details for debugging
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.error('Original error:', error);
                    // eslint-disable-next-line no-console
                    console.error('Error info:', errorInfo);
                }
            } finally {
                // CRITICAL: Always call the original onError handler, even if Sentry fails
                onError?.(error, errorInfo);
            }
        },
        [skipSentryCapture, transformSentryScope, boundaryName, onError]
    );

    // Determine effective fallback component/render function
    // Priority: FallbackComponent > fallback > fallbackRender > default
    const effectiveFallback: Partial<ReactErrorBoundaryProps> = FallbackComponent
        ? { FallbackComponent }
        : fallback
        ? { fallback }
        : fallbackRender
        ? { fallbackRender }
        : {
              FallbackComponent: (props: FallbackProps) => (
                  <DefaultErrorFallback {...props} test-id={toTestId(boundaryName)} />
              ),
          };

    return (
        <ReactErrorBoundary
            {...reactErrorBoundaryProps}
            {...effectiveFallback}
            onError={handleError}
        />
    );
}

/**
 * Re-export utilities from react-error-boundary for convenience.
 * This allows importing everything from a single module.
 */
export { useErrorBoundary, withErrorBoundary };
export type { FallbackProps };

/**
 * Helper HOC that wraps a component with ErrorBoundary.
 * Useful for wrapping components with consistent error handling.
 *
 * @example
 * ```tsx
 * const ProtectedComponent = withAppErrorBoundary(MyComponent, {
 *   fallback: <ErrorPage />,
 *   boundaryName: 'MyComponent',
 * });
 * ```
 */
export function withAppErrorBoundary<P extends Record<string, unknown>>(
    Component: ComponentType<P>,
    errorBoundaryProps?: AppErrorBoundaryProps
): ComponentType<P> {
    const WrappedComponent = (props: P) => (
        <ErrorBoundary {...errorBoundaryProps}>
            <Component {...props} />
        </ErrorBoundary>
    );

    WrappedComponent.displayName = `withAppErrorBoundary(${
        Component.displayName || Component.name || 'Component'
    })`;

    return WrappedComponent;
}
